import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { WhatsAppApi } from '../../api/resources';
import { errorMessage, validationErrors } from '../../api/client';
import { useCan } from '../../components/Can';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import TemplateFields, { emptyTemplateValue } from './TemplateFields';
import { fmtDate } from '../crm/useCrmLookups';

const LIST_POLL_MS = 10000;
const MSG_POLL_MS = 5000;
const FILTERS = [
  ['all', 'All'],
  ['mine', 'Mine'],
  ['unassigned', 'Unassigned'],
  ['unread', 'Unread'],
];

const title = (c) => c.contact_name || `+${c.phone}`;
const shortTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { day: 'numeric', month: 'short' });
};

function WindowTimer({ seconds }) {
  const expiresAt = useMemo(() => Date.now() + seconds * 1000, [seconds]);
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);
  const left = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
  if (left <= 0) return <span className="wa-timer bad">Reply window closed</span>;
  const h = Math.floor(left / 3600);
  const m = Math.floor((left % 3600) / 60);
  const cls = left < 3600 ? 'bad' : left < 3 * 3600 ? 'warn' : 'ok';
  return <span className={`wa-timer ${cls}`}>⏱ {h > 0 ? `${h}h ${m}m` : `${m}m`} left to reply</span>;
}

function Ticks({ status }) {
  if (status === 'failed') return <span className="wa-tick bad">!</span>;
  if (status === 'read') return <span className="wa-tick read">✓✓</span>;
  if (status === 'delivered') return <span className="wa-tick">✓✓</span>;
  if (status === 'sent') return <span className="wa-tick">✓</span>;
  return null;
}

function Media({ chatId, message }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let dead = false;
    let obj;
    WhatsAppApi.mediaBlob(chatId, message.id)
      .then((b) => {
        if (dead) return;
        obj = URL.createObjectURL(b);
        setUrl(obj);
      })
      .catch(() => !dead && setFailed(true));
    return () => {
      dead = true;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [chatId, message.id]);

  if (failed) return <em className="muted">Media unavailable (expired or could not be downloaded)</em>;
  if (!url) return <em className="muted">Loading…</em>;
  const mime = message.media?.mime || '';
  if (mime.startsWith('image/'))
    return (
      <a href={url} target="_blank" rel="noreferrer">
        <img className="wa-img" src={url} alt="" />
      </a>
    );
  if (mime.startsWith('audio/')) return <audio src={url} controls />;
  if (mime.startsWith('video/')) return <video className="wa-img" src={url} controls />;
  return (
    <a href={url} download={message.media?.filename || 'file'}>
      📄 {message.media?.filename || 'Download file'}
    </a>
  );
}

function NewChatModal({ templates, initial, onClose, onStarted }) {
  const [phone, setPhone] = useState(initial.phone || '');
  const [name, setName] = useState(initial.name || '');
  const [tpl, setTpl] = useState(emptyTemplateValue);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    setBanner('');
    try {
      const res = await WhatsAppApi.initiate({
        phone,
        contact_name: name,
        template_id: tpl.templateId,
        variables: tpl.vars,
        header_image: tpl.headerFile,
      });
      onStarted(res.chat_id);
    } catch (err) {
      setErrors(validationErrors(err));
      setBanner(errorMessage(err, 'Could not start the chat.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="New WhatsApp chat"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Sending…' : 'Send template'}
          </button>
        </>
      }
    >
      {banner && <div className="notice notice-error">{banner}</div>}
      <p className="muted" style={{ marginTop: 0 }}>
        A conversation starts with an approved template. Free text opens once the customer replies.
      </p>
      <form onSubmit={submit}>
        <div className="field">
          <label>Phone (with country code) *</label>
          <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="919876543210" />
          {errors.phone && <div className="err">{errors.phone}</div>}
        </div>
        <div className="field">
          <label>Contact name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <TemplateFields templates={templates} value={tpl} onChange={setTpl} errors={errors} />
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

export default function ChatsPage() {
  const canInitiate = useCan('whatsapp.chats.initiate');
  const canAssign = useCan('whatsapp.chats.assign');
  const [params, setParams] = useSearchParams();

  const [chats, setChats] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [staff, setStaff] = useState([]);
  const [error, setError] = useState('');
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [useTpl, setUseTpl] = useState(false);
  const [tpl, setTpl] = useState(emptyTemplateValue);
  const [sending, setSending] = useState(false);
  const [modal, setModal] = useState(null); // { initial } | 'assign'
  const [pendingPhone] = useState(() => params.get('phone'));
  const [pendingName] = useState(() => params.get('name'));
  const lastId = useRef(0);
  const bottom = useRef(null);
  const fileInput = useRef(null);

  const loadChats = useCallback(async () => {
    try {
      setChats(await WhatsAppApi.chats({ filter: filter === 'all' ? undefined : filter, search: search || undefined }));
    } catch (err) {
      setError(errorMessage(err, 'Could not load chats.'));
    }
  }, [filter, search]);

  useEffect(() => {
    loadChats();
    const t = setInterval(loadChats, LIST_POLL_MS);
    return () => clearInterval(t);
  }, [loadChats]);

  useEffect(() => {
    WhatsAppApi.templates({ approved_only: 1 }).then(setTemplates).catch(() => {});
    if (canAssign) WhatsAppApi.staffOptions().then(setStaff).catch(() => {});
  }, [canAssign]);

  // Open the chat for ?phone=… (from a lead) or offer to start one.
  const handledPhone = useRef(false);
  useEffect(() => {
    if (!pendingPhone || handledPhone.current) return;
    handledPhone.current = true;
    const digits = pendingPhone.replace(/\D/g, '');
    WhatsAppApi.chats({ search: digits.slice(-10) })
      .then((found) => {
        const hit = found.find((c) => c.phone.endsWith(digits.slice(-10)));
        if (hit) setSelectedId(hit.id);
        else if (canInitiate) setModal({ initial: { phone: digits, name: pendingName || '' } });
      })
      .catch(() => {})
      .finally(() => setParams({}, { replace: true }));
  }, [pendingPhone, pendingName, canInitiate, setParams]);

  // Load + poll the open conversation.
  useEffect(() => {
    lastId.current = 0;
    setMessages([]);
    setSelected(null);
    setUseTpl(false);
    if (!selectedId) return undefined;
    let dead = false;
    const pull = async (initial) => {
      try {
        const data = await WhatsAppApi.messages(selectedId, initial ? 0 : lastId.current);
        if (dead) return;
        setSelected(data.chat);
        if (data.messages.length) {
          lastId.current = data.messages[data.messages.length - 1].id;
          setMessages((prev) => (initial ? data.messages : [...prev, ...data.messages]));
        }
      } catch (err) {
        if (!dead && initial) setError(errorMessage(err, 'Could not open the chat.'));
      }
    };
    pull(true);
    const t = setInterval(() => pull(false), MSG_POLL_MS);
    return () => {
      dead = true;
      clearInterval(t);
    };
  }, [selectedId]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, selectedId]);

  const windowOpen = selected?.window_open;
  const sendAsTemplate = useTpl || (selected && !windowOpen);

  const afterSend = async () => {
    const data = await WhatsAppApi.messages(selectedId, lastId.current);
    setSelected(data.chat);
    if (data.messages.length) {
      lastId.current = data.messages[data.messages.length - 1].id;
      setMessages((prev) => [...prev, ...data.messages]);
    }
    loadChats();
  };

  const send = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    setError('');
    try {
      if (sendAsTemplate) {
        await WhatsAppApi.send(selectedId, {
          template_id: tpl.templateId,
          variables: tpl.vars,
          header_image: tpl.headerFile,
        });
        setTpl(emptyTemplateValue);
        setUseTpl(false);
      } else {
        await WhatsAppApi.send(selectedId, { body: text, file });
        setText('');
        setFile(null);
        if (fileInput.current) fileInput.current.value = '';
      }
      await afterSend();
    } catch (err) {
      setError(errorMessage(err, 'Could not send.'));
      afterSend().catch(() => {});
    } finally {
      setSending(false);
    }
  };

  const act = async (fn) => {
    setError('');
    try {
      await fn();
      const data = await WhatsAppApi.messages(selectedId, lastId.current);
      setSelected(data.chat);
      loadChats();
    } catch (err) {
      setError(errorMessage(err, 'Action failed.'));
    }
  };

  const canSend = sendAsTemplate ? !!tpl.templateId : !!(text.trim() || file);

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">WhatsApp</h1>
          <p className="page-sub">Chat with leads and customers from your business number.</p>
        </div>
        {canInitiate && (
          <button className="btn btn-primary" onClick={() => setModal({ initial: {} })}>
            <Icon name="plus" size={16} />
            New chat
          </button>
        )}
      </div>

      {error && <div className="notice notice-error">{error}</div>}

      <div className={selectedId ? 'wa-shell has-open' : 'wa-shell'}>
        <aside className="wa-list card">
          <div className="wa-list-head">
            <div className="search">
              <Icon name="search" />
              <input type="search" placeholder="Search name or phone…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="tabs" style={{ margin: '10px 0 0' }}>
              {FILTERS.map(([k, label]) => (
                <button key={k} className={filter === k ? 'tab active' : 'tab'} onClick={() => setFilter(k)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <ul>
            {chats.map((c) => (
              <li key={c.id}>
                <button className={c.id === selectedId ? 'wa-item active' : 'wa-item'} onClick={() => setSelectedId(c.id)}>
                  <div className="wa-item-top">
                    <strong>{title(c)}</strong>
                    <span className="muted">{shortTime(c.last_message_at)}</span>
                  </div>
                  <div className="wa-item-bottom">
                    <span className="wa-preview-line">{c.last_message_preview || '—'}</span>
                    {c.unread_count > 0 && <span className="wa-unread">{c.unread_count}</span>}
                  </div>
                  <div className="sub">
                    {c.assignee ? c.assignee.name : 'Unassigned'}
                    {c.lead ? ' · Lead' : ''}
                  </div>
                </button>
              </li>
            ))}
            {chats.length === 0 && <li className="empty"><strong>No chats</strong></li>}
          </ul>
        </aside>

        <section className="wa-pane card">
          {!selectedId && (
            <div className="empty">
              <div className="empty-icon"><Icon name="chat" size={26} /></div>
              <strong>Select a chat</strong>
              Choose a conversation on the left.
            </div>
          )}

          {selectedId && (
            <>
              <header className="wa-pane-head">
                <button className="icon-btn only-mobile" onClick={() => setSelectedId(null)} aria-label="Back">
                  <Icon name="chevron" />
                </button>
                <div>
                  <strong>{selected ? title(selected) : '…'}</strong>
                  <div className="sub">
                    {selected && `+${selected.phone}`}
                    {selected?.lead && (
                      <>
                        {' · '}
                        <Link to={`/crm/leads/${selected.lead.id}`}>Lead: {selected.lead.name}</Link>
                      </>
                    )}
                  </div>
                </div>
                <div className="spacer" />
                {selected && (windowOpen ? <WindowTimer seconds={selected.window_seconds_left} /> : <span className="wa-timer bad">Reply window closed</span>)}
                {selected && (
                  <button
                    className={selected.bot_paused ? 'btn btn-sm' : 'btn btn-sm btn-primary'}
                    onClick={() => act(() => WhatsAppApi.toggleBot(selected.id, !selected.bot_paused))}
                    title="Pause or resume the auto-reply bot for this chat"
                  >
                    <Icon name="bot" size={14} />
                    {selected.bot_paused ? 'Bot paused' : 'Bot on'}
                  </button>
                )}
                {selected && canAssign && (
                  <button className="btn btn-sm" onClick={() => setModal('assign')}>
                    {selected.assignee ? selected.assignee.name : 'Assign'}
                  </button>
                )}
              </header>

              <div className="wa-messages">
                {messages.map((m) => (
                  <div key={m.id} className={`wa-msg ${m.direction === 'out' ? 'out' : 'in'}`}>
                    {m.media && <Media chatId={selectedId} message={m} />}
                    {m.body && <p>{m.body}</p>}
                    <div className="wa-meta">
                      {m.is_bot ? '🤖 ' : m.sent_by ? `${m.sent_by.name} · ` : ''}
                      {fmtDate(m.created_at)} {m.direction === 'out' && <Ticks status={m.status} />}
                    </div>
                    {m.status === 'failed' && m.error && <div className="err">{m.error}</div>}
                  </div>
                ))}
                <div ref={bottom} />
              </div>

              <form className="wa-composer" onSubmit={send}>
                {sendAsTemplate ? (
                  <div className="wa-tpl-box">
                    {!windowOpen && (
                      <p className="muted" style={{ marginTop: 0 }}>
                        The 24-hour window is closed — send an approved template to continue.
                      </p>
                    )}
                    <TemplateFields templates={templates} value={tpl} onChange={setTpl} />
                    {windowOpen && (
                      <button type="button" className="btn btn-sm" onClick={() => setUseTpl(false)}>
                        Back to message
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {file && (
                      <div className="wa-file">
                        📎 {file.name}
                        <button type="button" className="icon-btn" onClick={() => { setFile(null); fileInput.current.value = ''; }}>
                          <Icon name="close" size={14} />
                        </button>
                      </div>
                    )}
                    <div className="wa-composer-row">
                      <input ref={fileInput} type="file" hidden onChange={(e) => setFile(e.target.files[0] || null)} />
                      <button type="button" className="icon-btn" title="Attach file" onClick={() => fileInput.current.click()}>
                        <Icon name="clip" />
                      </button>
                      <button type="button" className="icon-btn" title="Send a template" onClick={() => setUseTpl(true)}>
                        <Icon name="doc" />
                      </button>
                      <textarea
                        rows={1}
                        placeholder="Type a message…"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) send(e);
                        }}
                      />
                      <button className="btn btn-primary" disabled={sending || !canSend}>
                        <Icon name="send" size={16} />
                      </button>
                    </div>
                  </>
                )}
                {sendAsTemplate && (
                  <div style={{ textAlign: 'right' }}>
                    <button className="btn btn-primary" disabled={sending || !canSend}>
                      {sending ? 'Sending…' : 'Send template'}
                    </button>
                  </div>
                )}
              </form>
            </>
          )}
        </section>
      </div>

      {modal?.initial && (
        <NewChatModal
          templates={templates}
          initial={modal.initial}
          onClose={() => setModal(null)}
          onStarted={(id) => {
            setModal(null);
            loadChats();
            if (id) setSelectedId(id);
          }}
        />
      )}

      {modal === 'assign' && selected && (
        <Modal title="Assign chat" onClose={() => setModal(null)}>
          <div className="field">
            <label>Assign to</label>
            <select
              defaultValue={selected.assignee?.id || ''}
              onChange={(e) => {
                if (!e.target.value) return;
                setModal(null);
                act(() => WhatsAppApi.assign(selected.id, Number(e.target.value)));
              }}
            >
              <option value="">Select staff…</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          {selected.assignee && (
            <button
              className="btn"
              onClick={() => {
                setModal(null);
                act(() => WhatsAppApi.unassign(selected.id));
              }}
            >
              Unassign (bot answers again)
            </button>
          )}
        </Modal>
      )}
    </div>
  );
}
